/**
 * Trang vi.
 *
 * Diem nghiep vu quan trong nhat cua man hinh nay: BA SO DU HIEN RIENG.
 *
 * Gop chung thanh mot con so la loi nghiem trong — nguoi choi se tuong rut duoc
 * ca phan thuong chua hoan thanh vong cuoc va phan dang bi khoa cho lenh rut,
 * roi bam rut va bi tu choi ma khong hieu vi sao. May chu da tach ba thung so du
 * o tang so cai (NT-05); giao dien phai phan anh dung nhu vay.
 */

import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatMoney } from '../lib/money';
import { useSession, usePrimaryWallet } from '../lib/session';
import { Amount, Badge, Button, Card, EmptyState, Skeleton } from '../components/ui';
import styles from './Wallet.module.css';

export function WalletPage(): JSX.Element {
  const { isAuthenticated } = useSession();
  const wallet = usePrimaryWallet();

  const bonuses = useQuery({
    queryKey: ['bonuses'],
    queryFn: api.promotions.bonuses,
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) {
    return <EmptyState title="Đăng nhập để xem ví" />;
  }

  if (!wallet) {
    return (
      <div className={styles.page}>
        <Skeleton height={140} />
      </div>
    );
  }

  const available = BigInt(wallet.available.amount_minor);
  const bonus = BigInt(wallet.bonus.amount_minor);
  const locked = BigInt(wallet.locked.amount_minor);

  return (
    <div className={styles.page}>
      <div className={styles.balances}>
        <div className={`${styles.balanceCard} ${styles.balanceCardPrimary}`}>
          <span className={styles.balanceLabel}>Số dư khả dụng</span>
          <Amount value={formatMoney(wallet.available)} currency={wallet.currency} size="lg" />
          <span className={styles.balanceNote}>Dùng để cược và rút được ngay</span>
          <div className={styles.balanceActions}>
            <Link to="/nap-tien"><Button size="sm">Nạp tiền</Button></Link>
            <Link to="/rut-tien"><Button size="sm" variant="secondary">Rút tiền</Button></Link>
          </div>
        </div>

        <div className={styles.balanceCard}>
          <span className={styles.balanceLabel}>Số dư thưởng</span>
          <Amount value={formatMoney(wallet.bonus)} currency={wallet.currency} size="md" />
          <span className={styles.balanceNote}>
            Dùng để cược được, <strong>chưa rút được</strong> cho tới khi hoàn thành điều kiện
            vòng cược
          </span>
        </div>

        <div className={styles.balanceCard}>
          <span className={styles.balanceLabel}>Đang bị khóa</span>
          <Amount value={formatMoney(wallet.locked)} currency={wallet.currency} size="md" />
          <span className={styles.balanceNote}>
            {locked > 0n
              ? 'Đang giữ cho lệnh rút đang chờ xử lý'
              : 'Không có khoản nào đang bị giữ'}
          </span>
        </div>
      </div>

      {wallet.frozen && (
        <div className={styles.frozen} role="alert">
          Ví đang tạm khóa. Vui lòng liên hệ bộ phận hỗ trợ.
        </div>
      )}

      <Card title="Thưởng đang hoạt động">
        {bonuses.isLoading ? (
          <Skeleton height={64} />
        ) : bonuses.data && bonuses.data.bonuses.length > 0 ? (
          <ul className={styles.bonusList}>
            {bonuses.data.bonuses.map((b) => {
              const required = BigInt(b.wagering_required.amount_minor);
              const progress = BigInt(b.wagering_progress.amount_minor);
              const percent =
                required > 0n ? Number((progress * 100n) / required) : 100;

              return (
                <li key={b.bonus_id} className={styles.bonus}>
                  <div className={styles.bonusTop}>
                    <span className={styles.bonusName}>{b.campaign_code}</span>
                    <Amount value={formatMoney(b.amount)} currency={b.amount.currency} size="sm" />
                  </div>

                  {/* Hien ro CON THIEU bao nhieu, khong chi hien phan tram:
                      nguoi choi can biet con phai cuoc bao nhieu nua. */}
                  <div className={styles.progressRow}>
                    <div
                      className={styles.progressTrack}
                      role="progressbar"
                      aria-valuenow={Math.min(percent, 100)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Tiến độ vòng cược"
                    >
                      <div
                        className={styles.progressFill}
                        style={{ width: `${Math.min(percent, 100)}%` }}
                      />
                    </div>
                    <span className={`numeric ${styles.progressText}`}>
                      {formatMoney(b.wagering_progress)} / {formatMoney(b.wagering_required)}
                    </span>
                  </div>

                  <div className={styles.bonusFoot}>
                    <span className={styles.bonusRemaining}>
                      Còn thiếu{' '}
                      <strong className="numeric">{formatMoney(b.wagering_remaining)}</strong>{' '}
                      {b.amount.currency}
                    </span>
                    {b.max_bet_while_active && (
                      // BNS-R14: vuot tran nay thi thuong bi huy — phai canh bao truoc.
                      <Badge tone="warning">
                        Cược tối đa {formatMoney(b.max_bet_while_active)}
                      </Badge>
                    )}
                    <span className={styles.bonusExpiry}>
                      Hết hạn {new Date(b.expires_at).toLocaleDateString('vi-VN')}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={styles.noBonus}>Không có thưởng nào đang hoạt động.</p>
        )}
      </Card>

      {bonus > 0n && (
        <p className={styles.hint}>
          Muốn rút tiền ngay? Có thể hủy thưởng để rút phần tiền thật — phần thưởng còn lại sẽ bị
          thu hồi, tiền thật giữ nguyên.{' '}
          <Link to="/rut-tien" className={styles.hintLink}>Xem chi tiết</Link>
        </p>
      )}

      {available === 0n && bonus === 0n && locked === 0n && (
        <EmptyState
          title="Ví đang trống"
          description="Nạp tiền để bắt đầu."
          action={<Link to="/nap-tien"><Button>Nạp tiền</Button></Link>}
        />
      )}
    </div>
  );
}
